
interface RateLimitEntry {
  attempts: number;
  lastAttempt: number;
  isBlocked: boolean;
}

class RateLimiter {
  private attempts: Map<string, RateLimitEntry> = new Map();
  private readonly maxAttempts = 5;
  private readonly windowMs = 15 * 60 * 1000; // 15 minutes
  private readonly blockDurationMs = 30 * 60 * 1000; // 30 minutes

  isBlocked(identifier: string): boolean {
    const entry = this.attempts.get(identifier);
    if (!entry) return false;

    const now = Date.now();
    
    // Check if block period has expired
    if (entry.isBlocked && (now - entry.lastAttempt) > this.blockDurationMs) {
      this.attempts.delete(identifier);
      return false;
    }

    return entry.isBlocked;
  }

  recordAttempt(identifier: string, isSuccessful: boolean): { blocked: boolean; remainingAttempts: number } {
    const now = Date.now();
    const entry = this.attempts.get(identifier) || { attempts: 0, lastAttempt: now, isBlocked: false };

    // Reset if window has expired
    if ((now - entry.lastAttempt) > this.windowMs) {
      entry.attempts = 0;
      entry.isBlocked = false;
    }

    if (isSuccessful) {
      // Reset on successful login
      this.attempts.delete(identifier);
      return { blocked: false, remainingAttempts: this.maxAttempts };
    }

    // Increment failed attempts
    entry.attempts += 1;
    entry.lastAttempt = now;

    if (entry.attempts >= this.maxAttempts) {
      entry.isBlocked = true;
    }

    this.attempts.set(identifier, entry);

    return {
      blocked: entry.isBlocked,
      remainingAttempts: Math.max(0,  this.maxAttempts - entry.attempts)
    };
  }

  getRemainingBlockTime(identifier: string): number {
    const entry = this.attempts.get(identifier);
    if (!entry || !entry.isBlocked) return 0;

    const now = Date.now();
    const remaining = this.blockDurationMs - (now - entry.lastAttempt);
    return Math.max(0, remaining);
  }
}

export const authRateLimiter = new RateLimiter();
